import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { useApiCall } from '../hooks/useApi';
import useApi from '../hooks/useApi';
import { InlineSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorBoundary';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  
  const { execute: submitForm, loading: submitting, error: submitError, setError } = useApiCall();
  const { data: contactInfo, loading: infoLoading, error: infoError } = useApi(() => apiService.getContactInfo(), []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear any previous errors when user starts typing
    if (submitError) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await submitForm(() => apiService.submitContactForm(formData));
      setIsSubmitted(true);
      setSubmitMessage(response.message || "Thank you for your message! We'll get back to you within 24 hours.");
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setSubmitMessage('');
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      // Error is automatically handled by useApiCall hook
    }
  };

  // Default contact info as fallback
  const defaultContactInfo = {
    email: "info@transpost.co",
    phone: "+1 (555) 123-4567",
    address: "Suite 4, Level 8, Time Square Building, Andheri - Kurla Rd, B Wing, Chimatpada, Marol, Andheri East, Mumbai, Maharashtra 400059",
    working_hours: "Monday - Friday: 9:00 AM - 6:00 PM PST"
  };

  const displayContactInfo = contactInfo || defaultContactInfo;

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="h-1 w-12 bg-[#2aa996]"></div>
            <span className="text-[#2aa996] font-semibold uppercase tracking-wider text-sm">
              Get In Touch
            </span>
            <div className="h-1 w-12 bg-[#2aa996]"></div>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Let's <span className="gradient-text">Transform</span> Your Business
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to take your business to the next level? Contact us today for a free consultation 
            and discover how we can help you achieve your technology goals.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="animate-fadeInLeft">
            <h3 className="text-2xl font-bold mb-8 text-gray-800">
              Get in Touch
            </h3>
            
            {infoError && (
              <div className="mb-6">
                <ErrorMessage message="Unable to load contact information" />
              </div>
            )}
            
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#2aa996] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Email Us</h4>
                  <p className="text-gray-600">{displayContactInfo.email}</p>
                  <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                </div>
              </div>

             

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#2aa996] rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-1">Visit Us</h4>
                  <p className="text-gray-600">{displayContactInfo.address}</p>
                  <p className="text-sm text-gray-500">By appointment only</p>
                </div>
              </div>

             
            </div>

            {/* Why Choose Us */}
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="font-bold text-gray-800 mb-4">Why Choose Transpost?</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#2aa996] flex-shrink-0" size={16} />
                  <span className="text-gray-600 text-sm">Free consultation and project assessment</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#2aa996] flex-shrink-0" size={16} />
                  <span className="text-gray-600 text-sm">Transparent pricing with no hidden costs</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#2aa996] flex-shrink-0" size={16} />
                  <span className="text-gray-600 text-sm">Dedicated project manager for each client</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="text-[#2aa996] flex-shrink-0" size={16} />
                  <span className="text-gray-600 text-sm">Post-delivery support and maintenance</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="animate-fadeInRight">
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-8 text-gray-800">
                Start Your Project
              </h3>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-[#2aa996] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-white" size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-gray-800 mb-2">
                    Thank You!
                  </h4>
                  <p className="text-gray-600">
                    {submitMessage}
                  </p>
                </div>
              ) : (
                <>
                  {submitError && (
                    <div className="mb-6">
                      <ErrorMessage message={submitError} />
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          disabled={submitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2aa996] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          disabled={submitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2aa996] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          disabled={submitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2aa996] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                          placeholder="Your Company"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">
                          Service Interested In
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          disabled={submitting}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2aa996] focus:border-transparent transition-all duration-300 disabled:opacity-50"
                        >
                          <option value="">Select a service</option>
                          <option value="application-development">Application Development</option>
                          <option value="cybersecurity">Cybersecurity</option>
                          <option value="ai-transformation">AI Transformation</option>
                          <option value="cloud-migration">Cloud Migration</option>
                          <option value="devops">DevOps & Automation</option>
                          <option value="digital-transformation">Digital Transformation</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Project Details *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        disabled={submitting}
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2aa996] focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50"
                        placeholder="Tell us about your project requirements, timeline, and any specific challenges you're facing..."
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-primary flex items-center justify-center space-x-2 text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <InlineSpinner size="sm" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <Send size={20} />
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
